import { useState, useEffect } from 'react';
import { useAppContext } from '../lib/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../components/ui/use-toast';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useLocation } from 'wouter';

export default function ChangePassword() {
  const { auth } = useAppContext();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [displayError, setDisplayError] = useState('');
  const [showDisplayError, setShowDisplayError] = useState(false);
  const [displaySuccess, setDisplaySuccess] = useState('');
  const [showDisplaySuccess, setShowDisplaySuccess] = useState(false);

  // Password validation function
  function validateNewPassword(password: string): string {
    if (password.length < 10) return 'Password must be at least 10 characters long and include uppercase, lowercase, numbers, and special characters.';
    if (!/[A-Z]/.test(password)) return 'Password must be at least 10 characters long and include uppercase, lowercase, numbers, and special characters.';
    if (!/[a-z]/.test(password)) return 'Password must be at least 10 characters long and include uppercase, lowercase, numbers, and special characters.';
    if (!/[0-9]/.test(password)) return 'Password must be at least 10 characters long and include uppercase, lowercase, numbers, and special characters.';
    if (!/[^A-Za-z0-9]/.test(password)) return 'Password must be at least 10 characters long and include uppercase, lowercase, numbers, and special characters.';
    return '';
  }

  useEffect(() => {
    if (displayError) {
      setShowDisplayError(true);
      const timer = setTimeout(() => {
        setShowDisplayError(false);
        setTimeout(() => setDisplayError(''), 500);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [displayError]);

  useEffect(() => {
    if (displaySuccess) {
      setShowDisplaySuccess(true);
      const timer = setTimeout(() => {
        setShowDisplaySuccess(false);
        setTimeout(() => setDisplaySuccess(''), 500);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [displaySuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setDisplayError('');

    try {
      // Validate passwords match first
      if (formData.newPassword !== formData.confirmPassword) {
        setDisplayError('Passwords do not match. Please make sure your new passwords match.');
        setIsLoading(false);
        return;
      }

      // Then validate new password requirements
      const newPasswordValidationError = validateNewPassword(formData.newPassword);
      if (newPasswordValidationError) {
        setDisplayError(newPasswordValidationError);
        setIsLoading(false);
        return;
      }

      // Check if new password is the same as the current password
      if (formData.currentPassword === formData.newPassword) {
        setDisplayError('You cannot reuse your current password. Please choose a new password.');
        setIsLoading(false);
        return;
      }

      // Re-authenticate user with current password
      if (!auth.user || !auth.user.email) {
        setDisplayError('Unable to verify your account. Please log in again.');
        setIsLoading(false);
        return;
      }
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: auth.user!.email,
        password: formData.currentPassword,
      });
      if (signInError) {
        setDisplayError('Current password is incorrect.');
        setIsLoading(false);
        return;
      }
      // Update the app's user context with the new user (if available)
      if (signInData && signInData.user && auth.setUser) {
        auth.setUser({
          ...auth.user,
          id: signInData.user.id,
          email: signInData.user.email ?? auth.user.email,
        });
      }

      // Update password in Supabase
      const { error } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (error) throw error;

      // Password updated successfully
      window.localStorage.setItem('passwordUpdated', '1');

      // Clear form and redirect to profile
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setLocation('/profile');
    } catch (error: any) {
      setDisplayError(error.message || 'Failed to update password. Please try again.');
      setIsLoading(false);
      return;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <div className="px-4 py-4 max-w-md mx-auto">
        <Card className="w-full backdrop-blur-sm bg-white/80 shadow-xl border-0 rounded-2xl ring-0 outline-none shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-center mb-6 relative">
              <button
                onClick={() => window.history.back()}
                className="mr-2 shadow rounded-full -ml-2 -mt-2 absolute left-0 bg-white/80 hover:bg-blue-50 transition-colors p-2"
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5 text-blue-700" />
              </button>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                Change Password
              </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="currentPassword" className="text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showPasswords ? 'text' : 'password'}
                    value={formData.currentPassword}
                    onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                    required
                    autoComplete="off"
                    className="w-full pr-10 transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200 border-0 ring-0 outline-none focus:outline-none focus:ring-0 focus:border-0 appearance-none bg-transparent p-0 m-0"
                  >
                    {showPasswords ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="newPassword" className="text-sm font-medium text-gray-700">
                  New Password
                </label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPasswords ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    required
                    autoComplete="off"
                    className="w-full pr-10 transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200 border-0 ring-0 outline-none focus:outline-none focus:ring-0 focus:border-0 appearance-none bg-transparent p-0 m-0"
                  >
                    {showPasswords ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showPasswords ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    autoComplete="off"
                    className="w-full pr-10 transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200 border-0 ring-0 outline-none focus:outline-none focus:ring-0 focus:border-0 appearance-none bg-transparent p-0 m-0"
                  >
                    {showPasswords ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white hover:from-blue-700 hover:to-blue-900 rounded-full"
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update Password'}
              </Button>

              {displayError && (
                <div
                  className={`mt-4 p-4 bg-red-50 rounded-lg flex items-center gap-2 animate-fade-in transition-opacity duration-500 ${showDisplayError ? 'opacity-100' : 'opacity-0'}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-red-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
                    />
                  </svg>
                  <p className="text-sm text-red-600 font-medium">{displayError}</p>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 